import React from 'react';
import type { NoteTemplate } from '../../../../../constants/noteTemplates';
import { disciplineForProviderType } from '../../../../../constants/disciplines';
import type { TemplateNoteFormData } from '../../model/noteTemplates';
import { CheckboxGroup, FieldsProps, InputField, Segmented, SelectField, TextAreaField } from './fieldParts';

/**
 * Phase 20E: the generic visit note form (social work, respiratory treatment, nutrition, behavioral
 * session). Fields come from the note template; the server checks the same rules.
 */
const TemplateNoteFields: React.FC<FieldsProps<TemplateNoteFormData> & { template: NoteTemplate; discipline?: string | null }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, template, discipline,
}) => {
  if (!template.disciplines.includes(disciplineForProviderType(discipline) || '')) {
    return (
      <p className="text-sm text-gray-600" data-testid="template-note-not-allowed">
        A {template.label.toLowerCase()} can only be added on a {template.disciplines.join(' or ')} visit.
      </p>
    );
  }
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-slate-700' };
  const set = (key: string) => (v: string | string[] | boolean) => onChange({ ...value, [key]: v });
  const str = (key: string) => (typeof value[key] === 'string' ? (value[key] as string) : '');
  return (
    <div className="space-y-2" data-testid="template-note-fields">
      {template.fields.map((f) => {
        const id = `note-${template.entryType.toLowerCase()}-${f.key}`;
        switch (f.type) {
          case 'textarea':
            return <TextAreaField key={f.key} {...common} id={id} label={f.name} required={f.required} rows={2} placeholder={f.placeholder}
              value={str(f.key)} onChange={set(f.key)} error={errors[f.key]} />;
          case 'select':
            return <SelectField key={f.key} {...common} id={id} label={f.name} required={f.required}
              options={(f.options || []).map((o) => ({ value: o, label: o }))} value={str(f.key)} onChange={set(f.key)} error={errors[f.key]} />;
          case 'multiselect':
            return (
              <div key={f.key}>
                <CheckboxGroup name={id} label={`${f.name}${f.required ? ' *' : ''}`} disabled={disabled}
                  options={(f.options || []).map((o) => ({ value: o, label: o }))}
                  value={Array.isArray(value[f.key]) ? (value[f.key] as string[]) : []} onChange={set(f.key)} />
                {errors[f.key] && (errors[f.key] !== 'Required' || showRequired) && (
                  <p className="text-xs text-red-600" data-testid={`error-${id}`}>{errors[f.key]}</p>
                )}
              </div>
            );
          case 'boolean':
            return <Segmented key={f.key} name={id} label={f.name} isMobile={isMobile} disabled={disabled} showRequired={showRequired}
              options={[{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]}
              value={value[f.key] === true ? 'YES' : value[f.key] === false ? 'NO' : ''} onChange={(v) => set(f.key)(v === 'YES')} />;
          case 'number':
            return <InputField key={f.key} {...common} id={id} label={f.name} required={f.required} type="number" inputMode="decimal"
              value={str(f.key)} onChange={set(f.key)} error={errors[f.key]} />;
          default:
            return <InputField key={f.key} {...common} id={id} label={f.name} required={f.required} placeholder={f.placeholder}
              value={str(f.key)} onChange={set(f.key)} error={errors[f.key]} />;
        }
      })}
    </div>
  );
};

export default TemplateNoteFields;
